import { useEffect } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useOutletContext } from "react-router";

import type { ExtractResource } from "@prequel/import-react";
import { useExtract } from "@prequel/import-react";
import { checkTabIsActive, PageHeading, Spinner, Tabs } from "@prequel/shared-components";

import Breadcrumbs from "@/components/Breadcrumbs";
import { useRequiredParam } from "@/layouts/Navigation/useRequiredParam";
import { useToast } from "@/providers/ToastProvider";

export type ExtractContext = {
  extract: ExtractResource;
};

export function useExtractContext(): ExtractContext {
  return useOutletContext<ExtractContext>();
}

const ExtractPage = () => {
  const datasetID = useRequiredParam("datasetID");
  const extractID = useRequiredParam("extractID");
  const { addToast } = useToast();
  const { data: extract, error, isLoading } = useExtract(extractID);

  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      addToast({ kind: "ERROR", message: "Failed to load extract." });
    }
  }, [error, addToast]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!extract) {
    return <Navigate to={`/import/datasets/${datasetID}`} replace />;
  }

  const base = `/import/datasets/${datasetID}/extracts/${extractID}`;
  const tabs = [
    <NavLink key="Details" to={base} className={checkTabIsActive} end>
      Details
    </NavLink>,
    <NavLink key="Records" to={`${base}/records`} className={checkTabIsActive} end>
      Records
    </NavLink>,
  ];

  return (
    <div className="max-w-4xl px-7 py-4">
      <PageHeading
        title="Extract Job"
        onGoBack={() => navigate(`/import/datasets/${datasetID}`)}
        breadcrumbs={<Breadcrumbs />}
      />
      <div className="flex-1">
        <div className="relative">
          <div className="pt-0 pb-16">
            <div className="pb-6">
              <Tabs tabs={tabs} />
              <Outlet context={{ extract }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtractPage;
